import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
}

interface Workspace {
  id: string;
  name: string;
  members: number;
  users: User[];
  is_owner: boolean;
  member_count: number;
  alias_email: string;
  role?: string;
}

interface GlobalContextProps {
  isMobile: boolean;
  setIsMobile: (isMobile: boolean) => void;
  workspaces: Workspace[];
  setWorkspaces: (workspaces: Workspace[]) => void;
  refreshWorkspaces: () => void;
  workspaceRefreshTrigger: number;
}

interface GlobalProviderProps {
  children: ReactNode;
}

const GlobalContext = createContext<GlobalContextProps | undefined>(undefined);

export const GlobalProvider: React.FC<GlobalProviderProps> = ({ children }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [workspaceRefreshTrigger, setWorkspaceRefreshTrigger] = useState(0);

  const refreshWorkspaces = useCallback(() => {
    setWorkspaceRefreshTrigger(prev => prev + 1);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <GlobalContext.Provider value={{ 
      isMobile, 
      setIsMobile, 
      workspaces, 
      setWorkspaces, 
      refreshWorkspaces, 
      workspaceRefreshTrigger 
    }}>
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobalContext = () => {
  const context = useContext(GlobalContext);
  if (context === undefined) {
    throw new Error('useGlobalContext must be used within a GlobalProvider');
  }
  return context;
};